import { useMutation, useReactiveVar } from "@apollo/client";
import { ErrorBoundary } from "@sentry/react";
import { Editor, JSONContent } from "@tiptap/core";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  MeetingParticipantNode,
  MeetingViewMeetingNodeNewPageFragmentFragment,
  TopicNodeNewPageFragmentFragment,
} from "types/graphql-schema";

import cache, { currentTiptapJWTVar, currentUserVar } from "@cache/cache";
import Avatar from "@components/avatar/avatar";
import Error from "@components/error/error";
import Loading from "@components/loading/loading";
import Tooltip from "@components/tooltip/tooltip";
import { TopicflowIcon } from "@components/topicflow-icon/topicflow-icon";
import { isEmptyValue } from "@components/wysiwyg/helpers";
import WYSIWYG from "@components/wysiwyg/wysiwyg";
import { tempCacheIdPrefix } from "@helpers/constants";
import { classNames } from "@helpers/css";
import { assertEdgesNonNull, parseStringToJSON } from "@helpers/helpers";

import { MeetingWebsocketProviderContext } from "../context";
import toggleTopicIndividualNoteVisibilityMutation from "../graphql/toggle-topic-individual-note-visibility-mutation";

const IndividualNotes = ({
  topic,
  meeting,
  participant,
  onModEnter,
}: {
  topic: TopicNodeNewPageFragmentFragment;
  meeting: MeetingViewMeetingNodeNewPageFragmentFragment;
  participant: Pick<MeetingParticipantNode, "participantEmail" | "id"> & {
    user?: { id: number; firstName: string; name: string } | null;
  };
  onModEnter?: () => void;
}) => {
  const [isReplacingNotes, setIsReplacingNotes] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const currentNotesRef = useRef<JSONContent | null>(null);
  // Set when we toggle private<->public so the effect below knows it needs
  // to replay the captured content into the new editor once the new
  // providerWebsocket has synced.
  const pendingReplayRef = useRef(false);
  const currentUser = currentUserVar();
  const isTemporary = String(topic.id).includes(tempCacheIdPrefix);
  const isFormalOneonone = !!meeting.meetingGroup?.isFormalOneonone;
  const individualNotes = assertEdgesNonNull(topic.individualNotes) || [];

  const participantIsCurrentUser = participant.user?.id === currentUser.id;
  const participantIndividualNote = individualNotes.find(
    ({ creator }) => creator.id === participant?.user?.id,
  );
  const isNoteHidden = participantIndividualNote
    ? participantIndividualNote.hidden
    : true; // if the current note is not visible, then it means that it is private therefore hidden.

  const noteMarkedAsPrivate = participantIsCurrentUser ? isNoteHidden : false;

  const note = participantIndividualNote?.notes || "";
  const canEdit = participantIsCurrentUser && topic.canUpdate.permission;

  let overlayValue = null;
  if (isFormalOneonone && isEmptyValue(note) && topic.linkedTemplateTopic?.id) {
    const defaultNotes =
      meeting.meetingGroup?.facilitator?.id === participant.user?.id
        ? topic.linkedTemplateTopic.defaultFacilitatorNotes
        : topic.linkedTemplateTopic.defaultSubjectNotes;
    if (defaultNotes) overlayValue = parseStringToJSON(defaultNotes);
  }

  const [toggleIndividualNoteVisibility, { loading: loadingMutation }] =
    useMutation(toggleTopicIndividualNoteVisibilityMutation);
  const loading = loadingMutation || isReplacingNotes;

  const handleCurrentUserToggleIndividualNotes = () => {
    // tracks current note content
    if (ref.current && participantIsCurrentUser) {
      const el = ref.current?.querySelector(
        ".ProseMirror.js-topic-discussion-notes-input",
      ) as Element & { editor: Editor };
      if (el.editor) {
        currentNotesRef.current = el.editor?.getJSON();
      }
    }
    // Mark that we need to replay the captured content. The actual replay
    // runs in the effect below once the new providerWebsocket is in scope
    // and has finished syncing — `onCompleted` would capture the stale
    // (pre-toggle) providerWebsocket in its closure.
    pendingReplayRef.current = true;
    const hidden = !noteMarkedAsPrivate;
    // Send the captured content along with the visibility flip so the DB row
    // is current the moment other participants refetch the topic. Otherwise
    // they may seed their editor from a stale notes value while the
    // Hocuspocus webhook is still in its 5s debounce window.
    toggleIndividualNoteVisibility({
      variables: {
        topicIds: [topic.id],
        hidden,
        notes: currentNotesRef.current
          ? JSON.stringify(currentNotesRef.current)
          : undefined,
      },
    });
  };

  const handleUpdateContent = ({ editor }: { editor: Editor }) => {
    if (canEdit && participantIndividualNote && providerWebsocket?.isSynced) {
      cache.modify({
        id: cache.identify(participantIndividualNote),
        fields: {
          notes() {
            return editor.getJSON();
          },
        },
        // without broadcast false, it would update the active queries right away and
        // re-render everytime the user makes a change.
        broadcast: false,
      });
    }
  };

  const suffix = noteMarkedAsPrivate ? "-private" : "";
  const userId = participant.user?.id;
  const websocketId = `meeting-${meeting?.id}-participant-${userId}${suffix}`;
  const websocketDocumentId = `topic-${topic.id}-participant-${userId}${suffix}`;
  const context = useContext(MeetingWebsocketProviderContext);
  const { ydoc, providerWebsocket } = context[`${websocketId}`] || {
    ydoc: undefined,
    providerWebsocket: null,
  };

  const handleReplaceCurrentNoteWithNewNote = useCallback(() => {
    const el = ref.current?.querySelector(
      ".ProseMirror.js-topic-discussion-notes-input",
    ) as Element & { editor: Editor };
    if (!el?.editor || !currentNotesRef.current) return;

    if (
      JSON.stringify(el.editor.getJSON()) !==
      JSON.stringify(currentNotesRef.current)
    ) {
      el.editor
        .chain()
        .clearContent()
        .setContent(currentNotesRef.current)
        .run();
    }

    currentNotesRef.current = null;
    pendingReplayRef.current = false;
    setIsReplacingNotes(false);
  }, []);

  // After a private<->public toggle, the WYSIWYG remounts against a different
  // Yjs document (suffix `-private` vs the public one). Wait for the new
  // providerWebsocket to sync, then replay the content captured before the
  // toggle so the new doc broadcasts the user's notes to other participants.
  useEffect(() => {
    if (
      !pendingReplayRef.current ||
      !currentNotesRef.current ||
      !providerWebsocket
    ) {
      return;
    }
    setIsReplacingNotes(true);
    if (providerWebsocket.isSynced) {
      // Defer one tick so the freshly-mounted editor has time to bind to
      // the new Yjs doc before we inject content.
      const timer = setTimeout(handleReplaceCurrentNoteWithNewNote, 1000);
      return () => clearTimeout(timer);
    }
    providerWebsocket.on("synced", handleReplaceCurrentNoteWithNewNote);
    return () => {
      providerWebsocket.off("synced", handleReplaceCurrentNoteWithNewNote);
    };
  }, [providerWebsocket, handleReplaceCurrentNoteWithNewNote]);

  const tiptapAiJwt = useReactiveVar(currentTiptapJWTVar);

  const uploadVariable = useMemo(() => ({ topicId: topic.id }), [topic.id]);
  const mentionsConfig = useMemo(
    () => ({
      meetingGroupId: meeting?.meetingGroup?.id,
      meetingId: meeting?.id,
    }),
    [meeting?.id, meeting?.meetingGroup?.id],
  );
  const extraContext = useMemo(
    () => ({
      topicId: topic.id,
      relatedTopicId: !noteMarkedAsPrivate ? topic.id : undefined,
      meetingId: meeting.id,
      meetingGroupId: meeting.meetingGroup?.id,
      organizationId: meeting.organization?.id,
      meetingDate: meeting.startDatetime ? meeting.startDatetime : undefined,
    }),
    [
      noteMarkedAsPrivate,
      meeting.id,
      meeting.meetingGroup?.id,
      meeting.organization?.id,
      meeting.startDatetime,
      topic.id,
    ],
  );

  return (
    <div
      key={participant.id}
      className={classNames(
        "rounded-lg border",
        (currentUser.id !== participant.user?.id ||
          !topic.canUpdate.permission) &&
          "bg-gray-50",
      )}
      ref={ref}
    >
      <div className="ml-4 mr-4 text-xs text-gray-500 mt-3 flex items-center justify-between gap-4">
        <div className="flex-1 flex items-center gap-1 tracking-tight">
          <Avatar user={participant.user} size={4} />
          <span>{participant.user?.firstName || participant.user?.name}</span>
        </div>
        {currentUser.id === participant.user?.id &&
          topic.canUpdate.permission && (
            <div className="flex items-start gap-1">
              <Tooltip text="This makes your notes only visible to you. The other participants will not see this note unless you share your screen.">
                <label className="rounded-b flex items-center gap-1 text-2xs tracking-tight">
                  <input
                    disabled={loading}
                    type="checkbox"
                    checked={noteMarkedAsPrivate}
                    onChange={handleCurrentUserToggleIndividualNotes}
                  />
                  <div>Make private</div>
                </label>
              </Tooltip>
            </div>
          )}
      </div>
      <div className="" aria-label="Meeting topic individual notes">
        <div>
          {isTemporary || ydoc === undefined || providerWebsocket === null ? (
            <div className="min-h-[54px] flex items-center justify-center">
              <Loading size="5" />
            </div>
          ) : isNoteHidden && !participantIsCurrentUser ? (
            <div className="italic text-xs text-gray-500 flex items-center gap-1 p-4">
              <TopicflowIcon type="eyeOff" className="h-4 w-4 text-gray-400" />
              Notes are private.
            </div>
          ) : (
            <ErrorBoundary
              fallback={
                <Error
                  title="An unexpected error occurred."
                  description={
                    "The editor could not be rendered. We have been notified. Please refresh the page."
                  }
                />
              }
            >
              <div className={classNames("relative")}>
                {loading && (
                  <div className="z-1 border bg-gray-50 rounded-lg absolute inset-3 flex items-center justify-center text-gray-500 text-sm gap-2">
                    <Loading size="4" /> Updating
                  </div>
                )}
                <div
                  id={websocketDocumentId}
                  // Do not remove the key with tiptapAiJwt, editor needs to be recreated when the tiptapAiJwt changes
                  key={`${websocketDocumentId}-${tiptapAiJwt}`}
                >
                  <WYSIWYG
                    key={websocketDocumentId}
                    enableComment={false}
                    value={parseStringToJSON(note)}
                    className={classNames(
                      "js-individual-notes js-meeting-topic-input",
                      "text-base ",
                      // important to have padding so the + hover button on each line is clickable, as well as detecting when to hide the add/drag buttons when cursor leaves the editor
                      // https://github.com/Topicflow/topicflow/pull/1314
                      "-ml-[30px] pl-[46px] pt-2 pr-1",
                      "pb-3", // adding bottom padding here so the white space is clickable
                      canEdit ? "min-h-11" : "min-h-4",
                    )}
                    editable={canEdit}
                    placeholder={canEdit ? "Add my notes..." : ""}
                    overlayValue={overlayValue}
                    showPlaceholderOnlyWhenEditable={false}
                    showPlusButton={canEdit}
                    uploadVariable={uploadVariable}
                    ydoc={ydoc}
                    providerWebsocket={providerWebsocket}
                    webSocketDocumentId={websocketDocumentId}
                    mentionsConfig={mentionsConfig}
                    extraContext={extraContext}
                    onUpdateContent={handleUpdateContent}
                    tiptapAiJwt={tiptapAiJwt}
                    onModEnter={onModEnter}
                  />
                </div>
              </div>
            </ErrorBoundary>
          )}
        </div>
      </div>
    </div>
  );
};

export default IndividualNotes;
